import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { BrandLogo } from '../../shared/BrandLogo'
import { useAuth } from '../AuthProvider'
import { api } from '../api'
import { Button } from '../../components/ui/Button'
import { GoogleSignInButton } from '../components/GoogleSignInButton'
import {
  REGISTER_COUNTRIES,
  applyCountryDialToPhone,
  dialForCountry,
} from '../registerCountries'

type Step = 'register' | 'verify' | 'done'

export function ClientRegisterPage() {
  const { isAuthenticated, loginWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<Step>('register')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('')
  const [verifyToken, setVerifyToken] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)
  const ibCode = (searchParams.get('ib') || searchParams.get('ref') || '').trim().toUpperCase()

  useEffect(() => {
    // Legacy emails used /app/register?verify=… — send them to the magic-link page.
    const legacy = (searchParams.get('verify') || searchParams.get('token') || '').trim()
    if (legacy) {
      navigate(`/app/verify-email?token=${encodeURIComponent(legacy)}`, { replace: true })
    }
  }, [searchParams, navigate])

  if (isAuthenticated) {
    return <Navigate to="/app" replace />
  }

  async function onGoogle(credential: string) {
    setBusy(true)
    setError('')
    setInfo('')
    try {
      await loginWithGoogle(credential, true, ibCode)
      navigate('/app', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  async function onRegister(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setInfo('')
    try {
      const res = await api.register({
        email: email.trim(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        country: country.trim(),
        ib_code: ibCode || undefined,
      })
      if (!res.success) {
        setError('Registration failed')
        return
      }
      setMaskedEmail(res.email || email.trim())
      setInfo('Account created. Open the verification link we emailed you to enter the Client Portal.')
      setStep('verify')
      if (res.devVerificationToken) {
        setVerifyToken(res.devVerificationToken)
        setInfo(
          'Account created. SMTP is unavailable locally — use the prefilled token below, or open the verify link.',
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setBusy(false)
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setInfo('')
    try {
      navigate(`/app/verify-email?token=${encodeURIComponent(verifyToken.trim())}`, {
        replace: true,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
      setBusy(false)
    }
  }

  const isRegister = step === 'register'

  return (
    <div className={`cp-auth cp-auth--register${isRegister ? '' : ' cp-auth--verify'}`}>
      <div className="cp-auth__card cp-auth__card--register">
        <BrandLogo className="cp-auth__logo" tone="light" />
        <p className="cp-eyebrow cp-auth__eyebrow">Client Panel</p>
        <h1 className="cp-auth__title">
          {isRegister ? 'Create Account' : step === 'done' ? 'Email verified' : 'Verify email'}
        </h1>
        {isRegister ? (
          <p className="cp-muted cp-auth__description">
            Create one account for the Perfect Predict Portal and MT5. Email verification is required.
            {ibCode ? (
              <>
                {' '}
                Partner referral: <strong>{ibCode}</strong>
              </>
            ) : null}
          </p>
        ) : (
          <p className="cp-muted cp-auth__description">
            Check {maskedEmail || 'your email'} and tap <strong>Verify Email</strong>. That link opens
            your Client Portal directly — no need to copy a token.
            {verifyToken
              ? ' Local/dev fallback: a token is prefilled below because SMTP is unavailable.'
              : null}
          </p>
        )}

        {info ? <p className="cp-ok">{info}</p> : null}

        {isRegister ? (
          <>
            <form className="cp-auth__form" onSubmit={(e) => void onRegister(e)} noValidate={false}>
              <div className="cp-auth__row">
                <label className="cp-field">
                  <span className="cp-field__label">First Name</span>
                  <input
                    id="register-first-name"
                    name="first_name"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    maxLength={64}
                  />
                </label>
                <label className="cp-field">
                  <span className="cp-field__label">Last Name</span>
                  <input
                    id="register-last-name"
                    name="last_name"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    maxLength={64}
                  />
                </label>
              </div>

              <label className="cp-field">
                <span className="cp-field__label">Email</span>
                <input
                  id="register-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>

              <label className="cp-field">
                <span className="cp-field__label">Password</span>
                <input
                  id="register-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  maxLength={128}
                />
              </label>

              <div className="cp-auth__row">
                <label className="cp-field">
                  <span className="cp-field__label">
                    Country <span className="cp-field__optional">(optional)</span>
                  </span>
                  <select
                    id="register-country"
                    name="country"
                    autoComplete="country-name"
                    value={country}
                    onChange={(e) => {
                      const next = e.target.value
                      setCountry(next)
                      const dial = dialForCountry(next)
                      if (dial) setPhone((prev) => applyCountryDialToPhone(prev, dial))
                    }}
                  >
                    <option value="">Select country</option>
                    {REGISTER_COUNTRIES.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name} ({c.dial})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cp-field">
                  <span className="cp-field__label">
                    Phone <span className="cp-field__optional">(optional)</span>
                  </span>
                  <input
                    id="register-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+971 …"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </label>
              </div>

              {error ? (
                <p className="cp-error cp-auth__alert" role="alert">
                  {error}
                </p>
              ) : null}

              <p className="cp-auth__risk-note">
                By creating an account, you acknowledge that trading involves risk and that returns
                are not guaranteed. Please review the{' '}
                <a href="https://www.perfectpredict.com/terms" target="_blank" rel="noreferrer">
                  Terms
                </a>
                ,{' '}
                <a href="https://www.perfectpredict.com/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://www.perfectpredict.com/risk-disclosure"
                  target="_blank"
                  rel="noreferrer"
                >
                  Risk Disclosure
                </a>
                .
              </p>

              <Button type="submit" className="cp-auth__submit" disabled={busy}>
                {busy ? 'Creating Account…' : 'Create Account'}
              </Button>
            </form>
            <GoogleSignInButton
              mode="signup"
              disabled={busy}
              divider="above"
              onCredential={onGoogle}
              onError={setError}
            />
          </>
        ) : (
          <div className="cp-auth__form">
            {error ? (
              <p className="cp-error cp-auth__alert" role="alert">
                {error}
              </p>
            ) : null}
            {!verifyToken ? (
              <p className="cp-ok">
                Waiting for you to open the email link. You can close this tab after verifying.
              </p>
            ) : (
              <form onSubmit={(e) => void onVerify(e)}>
                <label className="cp-field">
                  <span className="cp-field__label">Dev verification token</span>
                  <input
                    id="register-verify-token"
                    name="token"
                    value={verifyToken}
                    onChange={(e) => setVerifyToken(e.target.value)}
                    required
                    minLength={8}
                  />
                </label>
                <Button type="submit" className="cp-auth__submit" disabled={busy}>
                  {busy ? 'Opening…' : 'Open verify link'}
                </Button>
              </form>
            )}
          </div>
        )}

        <p className="cp-auth__signin">
          Already have an account? <Link to="/app/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
